// /teams/:teamId/projects/:projectId/drift — DriftReportView. Lists scans
// newest-first. Each scan expands to show drifts grouped by flow with
// confidence badges. Per-drift accept / dismiss; batch "Accept all
// high-confidence" action.
//
// Accept/dismiss writes the review fields on the drift doc only — the
// actual YAML patch happens via the worker's accept pipeline (or local
// `cairn accept` CLI). This page records intent.

import { useState } from 'react'
import { useAuthState } from 'react-firebase-hooks/auth'
import { useParams } from 'react-router-dom'
import { PageHeader } from '../../components/dashboard/PageHeader.jsx'
import { Button } from '../../components/ui/button.jsx'
import { useProject, useScanDrifts, useScans } from '../../lib/data-hooks.js'
import { auth } from '../../lib/firebase.js'
import { setDriftStatus, setDriftStatusBatch } from '../../lib/mutations.js'

const HIGH_CONFIDENCE_THRESHOLD = 0.9

const STATUS_STYLES = {
  pending: 'bg-[var(--color-sandstone)] text-[var(--color-slate)]',
  accepted: 'bg-[var(--color-passed)]/15 text-[var(--color-passed)]',
  dismissed: 'bg-[var(--color-schist)]/15 text-[var(--color-schist)]',
}

function fmtTime(t) {
  if (!t) return ''
  const ms = typeof t === 'string' ? new Date(t).getTime() : (t.toMillis?.() ?? Number(t))
  if (!ms) return ''
  return new Date(ms).toLocaleString()
}

function ConfidenceBadge({ confidence }) {
  const pct = Math.round((confidence ?? 0) * 100)
  let cls = 'bg-[var(--color-schist)]/15 text-[var(--color-schist)]'
  if (confidence >= HIGH_CONFIDENCE_THRESHOLD) {
    cls = 'bg-[var(--color-passed)]/15 text-[var(--color-passed)]'
  } else if (confidence >= 0.6) {
    cls = 'bg-[var(--color-running)]/15 text-[var(--color-running)]'
  } else {
    cls = 'bg-[var(--color-failed)]/15 text-[var(--color-failed)]'
  }
  return (
    <span
      className={`rounded-sm px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider ${cls}`}
    >
      {pct}%
    </span>
  )
}

function DriftRow({ drift, onAccept, onDismiss, busy }) {
  const status = drift.status ?? 'pending'
  const statusCls = STATUS_STYLES[status] ?? STATUS_STYLES.pending
  return (
    <li className="flex flex-wrap items-center gap-3 border-t border-[var(--color-sandstone)] px-5 py-3">
      <ConfidenceBadge confidence={drift.confidence} />
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium text-[var(--color-basalt)]">
          {drift.kind ?? 'drift'}
          {drift.nodeId && (
            <span className="ml-2 font-mono text-xs text-[var(--color-schist)]">
              @ {drift.nodeId}
            </span>
          )}
        </div>
        <div className="mt-0.5 font-mono text-xs text-[var(--color-schist)]">
          {drift.signalsMatched?.length
            ? `matched: ${drift.signalsMatched.join(', ')}`
            : 'no signals matched'}
        </div>
      </div>
      <span className={`rounded-sm px-2 py-0.5 text-[10px] uppercase tracking-wider ${statusCls}`}>
        {status}
      </span>
      <div className="flex gap-2">
        <Button
          size="sm"
          variant="primary"
          disabled={busy || status === 'accepted'}
          onClick={() => onAccept(drift)}
        >
          Accept
        </Button>
        <Button
          size="sm"
          variant="ghost"
          disabled={busy || status === 'dismissed'}
          onClick={() => onDismiss(drift)}
        >
          Dismiss
        </Button>
      </div>
    </li>
  )
}

function groupByFlow(drifts) {
  const out = new Map()
  for (const drift of drifts) {
    const key = drift.flow ?? drift.flowId ?? '(unscoped)'
    if (!out.has(key)) out.set(key, [])
    out.get(key).push(drift)
  }
  return out
}

function ScanCard({ scan, teamId, projectId, reviewerUid }) {
  const [open, setOpen] = useState(false)
  const { data: drifts, loading } = useScanDrifts(teamId, projectId, open ? scan.id : null)
  const [busyId, setBusyId] = useState(null)
  const [batchBusy, setBatchBusy] = useState(false)
  const [error, setError] = useState(null)

  async function handleStatus(drift, status) {
    setBusyId(drift.driftId ?? drift.id)
    setError(null)
    // Optimistic UI is implicit via Firestore snapshot listener; just await.
    try {
      await setDriftStatus({
        teamId,
        projectId,
        scanId: scan.id,
        driftId: drift.driftId ?? drift.id,
        status,
        reviewerUid,
      })
    } catch (err) {
      setError(err.message)
    } finally {
      setBusyId(null)
    }
  }

  async function handleAcceptHighConfidence() {
    const ids = drifts
      .filter(
        (d) => d.confidence >= HIGH_CONFIDENCE_THRESHOLD && (d.status ?? 'pending') === 'pending',
      )
      .map((d) => d.driftId ?? d.id)
    if (ids.length === 0) return
    setBatchBusy(true)
    setError(null)
    try {
      await setDriftStatusBatch({
        teamId,
        projectId,
        scanId: scan.id,
        driftIds: ids,
        status: 'accepted',
        reviewerUid,
      })
    } catch (err) {
      setError(err.message)
    } finally {
      setBatchBusy(false)
    }
  }

  const summary =
    scan.driftCount > 0
      ? `${scan.driftCount} drift${scan.driftCount === 1 ? '' : 's'}`
      : 'No drift detected'
  const grouped = open ? groupByFlow(drifts) : null
  const highConfPending = open
    ? drifts.filter(
        (d) => d.confidence >= HIGH_CONFIDENCE_THRESHOLD && (d.status ?? 'pending') === 'pending',
      ).length
    : 0

  return (
    <li className="overflow-hidden rounded-md border border-[var(--color-sandstone)] bg-[var(--color-limestone)]">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="flex w-full flex-wrap items-center justify-between gap-3 px-5 py-3 text-left hover:bg-[var(--color-sandstone)]/40"
      >
        <div>
          <div className="font-mono text-xs text-[var(--color-schist)]">{scan.id}</div>
          <div className="text-sm text-[var(--color-basalt)]">{fmtTime(scan.startedAt)}</div>
        </div>
        <div className="flex items-center gap-3">
          {scan.repoUrl && (
            <span className="font-mono text-[10px] text-[var(--color-schist)]">
              {scan.branch ?? 'main'} @ {scan.repoUrl.replace(/^https?:\/\//, '').slice(0, 32)}
            </span>
          )}
          <span
            className={`rounded-sm px-2 py-0.5 text-xs ${scan.driftCount > 0 ? 'bg-[var(--color-running)]/15 text-[var(--color-running)]' : 'bg-[var(--color-passed)]/15 text-[var(--color-passed)]'}`}
          >
            {summary}
          </span>
          <span
            aria-hidden="true"
            className={`text-xs text-[var(--color-schist)] transition-transform ${open ? 'rotate-90' : ''}`}
          >
            ▶
          </span>
        </div>
      </button>

      {open && (
        <div className="border-t border-[var(--color-sandstone)] bg-[var(--color-sandstone)]/20">
          {loading && (
            <p className="px-5 py-3 text-sm text-[var(--color-schist)]">Loading drifts…</p>
          )}
          {!loading && drifts.length === 0 && (
            <p className="px-5 py-3 text-sm text-[var(--color-schist)]">No drifts in this scan.</p>
          )}
          {drifts.length > 0 && (
            <>
              {highConfPending > 0 && (
                <div className="flex items-center justify-between gap-3 border-b border-[var(--color-sandstone)] px-5 py-2">
                  <span className="text-xs text-[var(--color-schist)]">
                    {highConfPending} high-confidence drift{highConfPending === 1 ? '' : 's'}{' '}
                    pending
                  </span>
                  <Button
                    size="sm"
                    variant="primary"
                    disabled={batchBusy}
                    onClick={handleAcceptHighConfidence}
                  >
                    {batchBusy ? 'Accepting…' : 'Accept all high-confidence'}
                  </Button>
                </div>
              )}
              {error && (
                <p className="px-5 py-2 text-xs text-[var(--color-failed)]" role="alert">
                  {error}
                </p>
              )}
              {[...grouped.entries()].map(([flow, flowDrifts]) => (
                <section key={flow}>
                  <h3 className="border-t border-[var(--color-sandstone)] bg-[var(--color-sandstone)]/40 px-5 py-2 text-xs font-medium uppercase tracking-wider text-[var(--color-schist)]">
                    {flow}
                  </h3>
                  <ul>
                    {flowDrifts.map((d) => (
                      <DriftRow
                        key={d.id}
                        drift={d}
                        busy={busyId === (d.driftId ?? d.id) || batchBusy}
                        onAccept={(drift) => handleStatus(drift, 'accepted')}
                        onDismiss={(drift) => handleStatus(drift, 'dismissed')}
                      />
                    ))}
                  </ul>
                </section>
              ))}
            </>
          )}
        </div>
      )}
    </li>
  )
}

export function DriftReport() {
  const { teamId, projectId } = useParams()
  const [user] = useAuthState(auth)
  const { data: project } = useProject(teamId, projectId)
  const { data: scans, loading, error } = useScans(teamId, projectId)

  return (
    <>
      <PageHeader
        breadcrumb={[
          { label: 'Teams', to: '/teams' },
          { label: teamId, to: `/teams/${teamId}` },
          { label: project?.name ?? projectId, to: `/teams/${teamId}/projects/${projectId}` },
          { label: 'Drift' },
        ]}
        title="Drift"
        subhead="Scans of this project, newest first. Expand a scan to review or accept drift patches."
      />

      <div className="mx-auto max-w-5xl px-6 py-8">
        {loading && <p className="text-sm text-[var(--color-schist)]">Loading scans…</p>}
        {error && (
          <p className="text-sm text-[var(--color-failed)]" role="alert">
            {error.message}
          </p>
        )}
        {!loading && scans.length === 0 && (
          <div className="rounded-md border border-dashed border-[var(--color-sandstone)] bg-[var(--color-sandstone)]/30 p-8 text-center">
            <p className="text-sm text-[var(--color-slate)]">
              No scans yet. Run <code className="font-mono">cairn scan</code> locally or enqueue a
              hosted scan from this project.
            </p>
          </div>
        )}
        {scans.length > 0 && (
          <ul className="space-y-3">
            {scans.map((scan) => (
              <ScanCard
                key={scan.id}
                scan={scan}
                teamId={teamId}
                projectId={projectId}
                reviewerUid={user?.uid}
              />
            ))}
          </ul>
        )}
      </div>
    </>
  )
}
